import React from 'react';

import { useIntl } from 'react-intl';

import { ImageSlide } from 'src/features/visitor/components/carousel/ImageCarousel';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from 'src/registry/ui/Carousel';
import { commonMessages } from 'src/utils/messages';
import { cn } from 'src/utils/shadcn';

type ImageCarouselInModalProps = {
    imageSlides: ImageSlide[];
    selectedSlide?: ImageSlide;
    setSelectedSlide?: (imageSlide: ImageSlide) => void;
};
const ImageCarouselInModal = ({ imageSlides = [], selectedSlide, setSelectedSlide }: ImageCarouselInModalProps) => {
    const { formatMessage } = useIntl();

    const indexOfSelectedSlide = selectedSlide ? imageSlides.indexOf(selectedSlide) : 0;
    const [activeIndex, setActiveIndex] = React.useState<number>(indexOfSelectedSlide);

    React.useEffect(() => {
        if (setSelectedSlide) {
            setSelectedSlide(imageSlides[activeIndex]);
        }
    }, [activeIndex, imageSlides, setSelectedSlide]);

    const thumbContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const container = thumbContainerRef.current;
        if (!container) return;
        const activeThumb = container.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
        if (activeThumb) {
            activeThumb.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    }, [activeIndex]);

    return (
        <div className="flex h-full max-w-full flex-col items-center justify-center">
            <div className="relative">
                <img
                    src={imageSlides[activeIndex]?.image ?? ''}
                    alt={imageSlides[activeIndex]?.title}
                    className="h-auto w-full rounded-2xl object-cover"
                />
            </div>
            {imageSlides.length > 1 && (
                <div className="relative mt-4 w-full">
                    <Carousel>
                        <CarouselContent className="flex p-2" ref={thumbContainerRef}>
                            {imageSlides.map((slide, index) => (
                                <CarouselItem
                                    key={`${slide.title}-${index}`}
                                    className="flex-shrink-0 basis-auto"
                                    data-index={index}
                                >
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveIndex(index);
                                            if (setSelectedSlide) setSelectedSlide(imageSlides[index]);
                                        }}
                                        className={cn(
                                            'h-[110px] w-[180px] cursor-pointer overflow-hidden rounded-lg border-2 transition-all duration-200',
                                            index === activeIndex
                                                ? 'border-primary scale-105'
                                                : 'border-transparent hover:scale-102',
                                        )}
                                    >
                                        <img
                                            src={slide.image ?? ''}
                                            alt={slide.title}
                                            className="h-full w-full object-cover"
                                        />
                                        {index === activeIndex && (
                                            <div className="bg-primary/20 pointer-events-none absolute inset-0 rounded-lg" />
                                        )}
                                    </button>
                                    <div className="flex flex-col items-center">
                                        <div className="text-sm font-medium">{slide.title}</div>
                                        <div className="text-muted-foreground text-xs">{slide.subtitle}</div>
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>

                        <div className="relative mt-6 flex flex-row items-center justify-between">
                            <div className="w-full">
                                <CarouselPrevious
                                    imageLength={imageSlides.length}
                                    onClick={() =>
                                        setActiveIndex(!activeIndex ? imageSlides.length - 1 : activeIndex - 1)
                                    }
                                    className="left-0 h-10 w-28 rounded-lg px-4 py-2"
                                    color="black"
                                    size="lg"
                                />
                            </div>
                            <div className="w-full text-center text-sm font-medium text-gray-600">{`${formatMessage(commonMessages.totalImages, { count: imageSlides.length })}`}</div>
                            <div className="w-full">
                                <CarouselNext
                                    onClick={() =>
                                        setActiveIndex(imageSlides.length - 1 === activeIndex ? 0 : activeIndex + 1)
                                    }
                                    className="right-0 h-10 w-28 rounded-lg px-4 py-2"
                                    color="black"
                                    size="lg"
                                />
                            </div>
                        </div>
                    </Carousel>
                </div>
            )}
        </div>
    );
};

export default ImageCarouselInModal;
